import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import { createAgentClient } from "../api/agentClient";
import type { ClinicalRuntime } from "../auth/ClinicalRuntimeProvider";
import { useOptionalDemoScheduling } from "../demo/DemoSchedulingProvider";
import { useClinicMessaging } from "../messaging/ClinicMessaging";
import {
  documentInboxRows,
  faxAttentionRows,
  type InboxBucket,
  type InboxRow,
  patientMessageAttentionRows,
  referralQueueRows,
  resultQueueRows,
  staffRequestRows,
} from "./documentInbox";

export type InboxScope = "mine" | "clinic" | "unassigned";
type InboxProfile = { userId: string; role: string };

/** The page and sidebar count the same authorized personal work, including coverage. */
export function isInAttentionScope(
  row: InboxRow,
  profile: InboxProfile | undefined,
  scope: InboxScope,
): boolean {
  if (!profile) return false;
  const effectiveScope = profile.role === "clinic_admin" ? scope : "mine";
  if (effectiveScope === "clinic") return true;
  if (effectiveScope === "unassigned") return row.ownerId === null;
  return (
    row.ownerId === profile.userId ||
    (row.kind === "staff" && row.ownerId === null) ||
    (row.kind === "patient_message" &&
      (profile.role === "front_desk" || profile.role === "clinic_admin")) ||
    row.coverageIds.includes(profile.userId)
  );
}

/** Shared sources keep the navigation count current even while Attention is closed. */
export function useAttentionInbox(
  runtime: ClinicalRuntime,
  { faxEnabled = true }: { faxEnabled?: boolean } = {},
) {
  const messaging = useClinicMessaging();
  const siteId = runtime.config.siteId;
  const client = useMemo(
    () =>
      createAgentClient({
        baseUrl: runtime.config.agentApiOrigin,
        getAccessToken: runtime.workforce.accessToken,
        siteId,
      }),
    [runtime.config.agentApiOrigin, runtime.workforce.accessToken, siteId],
  );
  const profile = useQuery({
    queryKey: ["workforce-profile"],
    queryFn: () => client.getWorkforceProfile(),
  });
  const inbox = useQuery({
    queryKey: ["document-inbox", siteId],
    queryFn: () => client.getDocumentInbox(),
    refetchInterval: 5000,
  });
  const staffRequests = useQuery({
    queryKey: ["staff-requests", siteId],
    queryFn: ({ signal }) => client.getStaffRequests(siteId, signal),
    refetchInterval: 30_000,
  });
  const resultQueue = useQuery({
    queryKey: ["order-result-work-queue", siteId],
    queryFn: ({ signal }) => client.listOrderResultWorkQueue(signal),
    refetchInterval: 30_000,
    retry: false,
  });
  const referralQueue = useQuery({
    queryKey: ["referral-work-queue", siteId],
    queryFn: ({ signal }) => client.listReferralWorkQueue(signal),
    refetchInterval: 30_000,
    retry: false,
  });
  const faxReceipts = useQuery({
    queryKey: ["fax-receipts", siteId],
    queryFn: () => client.listFaxReceipts(),
    enabled: faxEnabled,
    refetchInterval: 30_000,
    retry: false,
  });
  const faxConnection = useQuery({
    queryKey: ["fax-settings", siteId],
    queryFn: () => client.getFaxSettings(),
    enabled: faxEnabled,
    refetchInterval: 30_000,
    retry: false,
  });
  const faxTransmissions = useQuery({
    queryKey: ["fax-transmissions", siteId],
    queryFn: () => client.listFaxTransmissions(),
    enabled: faxEnabled,
    refetchInterval: 30_000,
    retry: false,
  });
  // Only clinics with the durable scheduling lane expose patient-message attention.
  const scheduling = useOptionalDemoScheduling();
  const patientMessagesOn =
    scheduling?.correctBookingContact !== undefined && scheduling.state !== null;
  const patientMessages = useQuery({
    queryKey: ["patient-message-attention", siteId],
    queryFn: ({ signal }) => client.getPatientMessageAttention(signal),
    enabled: patientMessagesOn,
    refetchInterval: 30_000,
    retry: false,
  });
  const role = profile.data?.role ?? "";
  const now = Date.now();
  const rows = [
    ...faxAttentionRows(faxReceipts.data ?? [], faxTransmissions.data ?? [], faxConnection.data),
    ...(patientMessagesOn ? patientMessageAttentionRows(patientMessages.data) : []),
    ...(inbox.data ? documentInboxRows(inbox.data, role, now, profile.data?.userId) : []),
    ...resultQueueRows(resultQueue.data?.items ?? [], role, now),
    ...referralQueueRows(referralQueue.data?.items ?? [], role, now, profile.data?.userId),
    ...staffRequestRows(
      (staffRequests.data ?? []).filter((request) => !messaging.enabled || !request.conversationId),
      now,
    ),
  ];
  return {
    client,
    profile,
    inbox,
    staffRequests,
    resultQueue,
    referralQueue,
    faxReceipts,
    faxConnection,
    faxTransmissions,
    scheduling,
    patientMessagesOn,
    patientMessages,
    rows,
  };
}

/** Load every authorized message page so sorting and counting never stop at a page boundary. */
export function useAttentionConversations(
  client: ReturnType<typeof createAgentClient>,
  siteId: string,
  {
    scope = "mine",
    bucket = "action",
    search = "",
  }: {
    scope?: "mine" | "teams" | "sent" | "reassignment";
    bucket?: InboxBucket;
    search?: string;
  } = {},
) {
  const messaging = useClinicMessaging();
  return useQuery({
    queryKey: ["clinic-messages", messaging.scope, scope, bucket, search],
    queryFn: async ({ signal }) => {
      const pages: Awaited<ReturnType<typeof client.listClinicConversations>>[] = [];
      const cursors = new Set<string>();
      let cursor: string | undefined;
      do {
        const page = await client.listClinicConversations(
          { siteId, scope, bucket, search, limit: 100, ...(cursor ? { cursor } : {}) },
          signal,
        );
        pages.push(page);
        cursor = page.nextCursor ?? undefined;
        if (cursor && cursors.has(cursor))
          throw new Error("Messages could not be fully loaded. Refresh to retry.");
        if (cursor) cursors.add(cursor);
      } while (cursor);
      return { pages };
    },
    enabled: messaging.enabled,
    refetchInterval: 5000,
    refetchOnWindowFocus: "always",
    retry: false,
  });
}
